"use client";

import * as React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface CustomFileInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  buttonText?: string;
  noFileText?: string;
  buttonClassName?: string;
  labelClassName?: string;
}

const CustomFileInput = React.forwardRef<HTMLInputElement, CustomFileInputProps>(
  ({ 
    className, 
    buttonText = "Choose File", 
    noFileText = "No file chosen", 
    onChange, 
    buttonClassName,
    labelClassName,
    ...props 
  }, ref) => {
    const internalRef = React.useRef<HTMLInputElement>(null);
    const [fileName, setFileName] = React.useState<string>("");

    // Combine the forwarded ref and internal ref
    React.useImperativeHandle(ref, () => internalRef.current as HTMLInputElement);

    const handleButtonClick = () => {
      internalRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        setFileName(e.target.files[0].name);
      } else {
        setFileName("");
      }
      // Call the original onChange handler if provided
      if (onChange) {
        onChange(e);
      }
    };

    return (
      <div className={cn("flex items-center gap-2", className)}>
        {/* Hidden actual file input */}
        <Input
          type="file"
          ref={internalRef}
          className="hidden"
          onChange={handleFileChange}
          {...props}
        />
        {/* Visible button */}
        <Button
          type="button" // Important to prevent form submission
          variant="outline"
          onClick={handleButtonClick}
          className={cn("shrink-0 dark:text-white dark:border-gray-600 dark:hover:bg-gray-700", buttonClassName)}
        >
          {buttonText}
        </Button>
        {/* Visible label */}
        <span className={cn("text-sm text-muted-foreground truncate dark:text-gray-300", labelClassName)}>
          {fileName || noFileText}
        </span>
      </div>
    );
  }
);
CustomFileInput.displayName = "CustomFileInput";

export { CustomFileInput }; 